import React, { useEffect, useState, useRef } from "react";
import SidebarTechTree from "./SidebarTechTree";
import EliteMarkdownViewer from "./EliteMarkdownViewer";
import ThemeToggle from "./ThemeToggle";
import EliteSelectionModal from "./EliteSelectionModal";
import PremiumUpgradeModal from "./PremiumUpgradeModal";
import {
  BookOpen,
  ChevronDown,
  Search,
  Sparkles,
  ArrowLeft,
  Mic
} from "lucide-react";
import ChatBot from "./SkillGenBot/ChatBot";
import NewVoiceAssistant from "./NewVoiceAssistant";
import { useTheme } from "../contexts/ThemeContext";
import { useSubscription } from "../hooks/useSubscription";
import { useTenant } from "../Context/TenantContext";
import { useNavigate, useParams } from "react-router-dom";
import API_BASE_URL from "../Config/config";

function slugify(str) {
  return str.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
}

const EliteTechnologyDetail = () => {
  const { tech } = useParams();
  const [technologies, setTechnologies] = useState([]);
  const [selected, setSelected] = useState({ tech: tech, topic: null, subtopic: null });
  const [mapping, setMapping] = useState(null);
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [lockedFile, setLockedFile] = useState(null);
  const [readingProgress, setReadingProgress] = useState(0);
  const [headings, setHeadings] = useState([]);
  const [activeHeading, setActiveHeading] = useState("");

  const { theme } = useTheme();
  const { isPaid, elite, isLoaded, plan } = useSubscription();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const isDark = theme === "dark";

  // If user is on a tech page they haven't selected, redirect them to the selection page
  useEffect(() => {
    if (isLoaded && hasSelection && tech !== hasSelection) {
      navigate(`/elite-mastery/${hasSelection}`);
    }
  }, [isLoaded, hasSelection, tech, navigate]);
  const [showVoice, setShowVoice] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const chatBotRef = useRef(null);
  const voiceAssistantRef = useRef(null);

  // Check if this is a tenant student (college flow) - they should have access
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const host = window.location.hostname;
  const isTenantHost = host !== 'theskillgenie.com' && 
                      host !== 'www.theskillgenie.com' && 
                      host !== 'localhost' &&
                      host !== '127.0.0.1';
  
  const isTenantStudent = (user && (user.role === 'student' || user.role === 'tenant-student' || user.role === 'admin') && (user.tenant || user.college_id || isTenantHost)) ||
                          (tenant && tenant.domain !== 'theskillgenie.com');

  // Determine user access level
  const hasPremiumAccess = isPaid || isTenantStudent || user.role === 'admin';
  const hasSelection = elite?.selection;

  // Final filtered list of technologies based on user's selection status
  const filteredTechnologies = technologies.filter(t => {
    // Strictly enforce selection: if they have a selection, only show that.
    // If they don't have a selection, they should be redirected or shown the modal.
    if (!hasSelection) return true; 
    return t.technology === hasSelection;
  });

  // Get user ID
  const getUserId = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return user?.userId || user?.user_id || null;
    } catch {
      return null;
    }
  };

  // Load Elite Technologies (java-premium and python-premium)
  useEffect(() => {
    const eliteTechs = [
      { technology: "java-premium", topics: [] },
      { technology: "python-premium", topics: [] }
    ];

    const loadTechMappings = async () => {
      const techs = [];
      const userId = getUserId();
      for (const t of eliteTechs) {
        try {
          const res = await fetch(`/docs/${t.technology}/mapping.json`);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const mappingData = await res.json();

          // Organize topics and subtopics
          const topicsMap = {};
          Object.entries(mappingData).forEach(([key, path]) => {
            const parts = path.split('/');
            const topicName = parts[0].replace(/_/g, ' ');
            const subtopicTitle = parts[parts.length - 1].replace(/_/g, ' ').replace('.md', '');
            
            if (!topicsMap[topicName]) {
              topicsMap[topicName] = { topic: topicName, subtopics: [] };
            }
            topicsMap[topicName].subtopics.push({
              title: subtopicTitle,
              file: `/docs/${t.technology}/${path}`
            });
          });

          techs.push({
            technology: t.technology,
            topics: Object.values(topicsMap)
          });
        } catch (err) {
          console.error(`Failed to load mapping for ${t.technology}:`, err);
        }
      }

      setTechnologies(techs);
    };

    loadTechMappings();
  }, []);

  // Check if user needs to make a selection
  useEffect(() => {
    if (!isLoaded) return;

    if (!hasSelection) {
      setShowSelectionModal(true);
    } else if (tech !== hasSelection) {
      // If user tries to access a tech they haven't selected, redirect to their selected one
      // Requirement: "no access for other"
      navigate(`/elite-mastery/${hasSelection}`);
    }
  }, [isLoaded, hasSelection, tech]);

  // Load mapping when technology selected
  useEffect(() => {
    if (!tech) return;
    fetch(`/docs/${tech}/mapping.json`)
      .then((res) => res.json())
      .then(setMapping)
      .catch(() => setMapping(null));
  }, [tech]);

  // Load initial scroll position from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const scroll = params.get('scroll');
    if (scroll && isLoaded) {
        const container = document.getElementById('mastery-content-area');
        if (container) {
            setTimeout(() => {
                container.scrollTop = parseInt(scroll);
            }, 1000);
        }
    }
  }, [isLoaded, selected.subtopic]);

  // Track reading progress
  useEffect(() => {
    const container = document.getElementById('mastery-content-area');
    if (!container || !selected.subtopic) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const progress = (scrollTop / (scrollHeight - clientHeight)) * 100;
      setReadingProgress(progress);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [selected.subtopic]);

  // Save progress periodically
  useEffect(() => {
    if (!selected.subtopic || !tech) return;

    const saveProgress = async () => {
      const userId = localStorage.getItem('user_id');
      if (!userId) return;

      try {
        await fetch(`${API_BASE_URL}/api/user/save-reading`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'user-id': userId
          },
          body: JSON.stringify({
            module: 'Elite Mastery',
            technology: tech,
            topic: selected.topic || 'General',
            subtopic: selected.subtopic,
            file_path: selected.subtopic,
            scroll_position: Math.round(readingProgress),
            reading_percentage: Math.round(readingProgress),
            time_spent: 30,
            status: readingProgress > 90 ? 'Completed' : 'In Progress'
          })
        });
        
        // Also update consistency
        const { updateLearningConsistency } = await import('../utils/consistency');
        updateLearningConsistency(userId);
      } catch (err) {
        console.error('Failed to save reading progress:', err);
      }
    };

    const interval = setInterval(saveProgress, 30000);
    return () => clearInterval(interval);
  }, [selected.subtopic, tech, readingProgress]);

  const handleSelectionSuccess = (selectedTech) => {
    navigate(`/elite-mastery/${selectedTech}`);
    window.location.reload();
  };

  const handleFileSelect = (file, isLocked) => {
    if (isLocked) {
      setLockedFile(file);
      setShowUpgradeModal(true);
    } else {
        setLockedFile(null);
        setSelected(prev => ({ ...prev, subtopic: file }));
    }
  };

  return (
    <div className={`${isDark ? "bg-[#0B0F1A]" : "bg-[#F8FAFC]"} min-h-screen flex flex-col font-sans selection:bg-primary/30`}>
      {/* 🧭 Top Navigation Bar */}
      <header className={`h-16 flex items-center justify-between px-6 sticky top-0 z-[70] border-b backdrop-blur-md ${
        isDark ? "bg-[#0B0F1A]/80 border-white/5" : "bg-gradient-to-r from-blue-500 to-blue-600 border-blue-700"
      }`}>
        <div className="flex items-center gap-4">
          <button 
            onClick={() => {
              // Determine the correct dashboard path based on domain/tenant
              const isMainPlatform = !tenant || 
                                   tenant.domain === 'theskillgenie.com' || 
                                   tenant.tenantKey === 'theskillgenie' ||
                                   window.location.hostname === 'theskillgenie.com' ||
                                   window.location.hostname === 'www.theskillgenie.com' ||
                                   window.location.hostname === 'localhost';
              
              const userType = localStorage.getItem('userType');
              
              if (isMainPlatform) {
                if (userType === 'student') {
                  navigate('/dashboard');
                } else if (userType === 'college') {
                  navigate('/admin/dashboard');
                } else {
                  navigate('/dashboard');
                }
              } else {
                if (userType === 'student') {
                  navigate('/student/dashboard');
                } else if (userType === 'college') {
                  navigate('/admin/dashboard');
                } else {
                  navigate('/dashboard');
                }
              }
            }}
            className={`p-2 rounded-lg transition-colors ${isDark ? "hover:bg-white/10 text-white" : "hover:bg-white/20 text-white"}`}
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shadow-lg ${isDark ? "bg-blue-500 shadow-blue-500/20" : "bg-white shadow-black/10"}`}>
              <Sparkles className={isDark ? "text-white" : "text-blue-600"} size={18} />
            </div>
            <span className={`text-lg font-bold tracking-tight ${isDark ? "text-white" : "text-white"}`}>
              {tech ? tech.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'Elite Technology Mastery'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button 
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className={`p-2 rounded-lg transition-colors ${isDark ? "hover:bg-white/10 text-white" : "hover:bg-white/20 text-white"}`}
            title={isSidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"}
          >
            <BookOpen size={20} />
          </button>
          <ThemeToggle />
          <button
            onClick={() => {
              setShowVoice(!showVoice);
              setShowChat(false);
            }}
            className={`p-2 rounded-lg transition-all ${
              showVoice 
                ? "bg-white text-blue-600 shadow-lg" 
                : isDark ? "hover:bg-white/10 text-white" : "hover:bg-white/20 text-white"
            }`}
            title="Voice Assistant"
          >
            <Mic size={20} />
          </button>
          {!hasPremiumAccess && (
             <button
                onClick={() => navigate('/skillgenpayments')}
                className="px-4 py-2 rounded-lg text-sm font-bold bg-white text-blue-600 hover:bg-blue-50 transition-all shadow-lg shadow-black/10"
              >
                Upgrade to Premium
              </button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {!isLoaded ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : !hasSelection ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="max-w-md space-y-6">
              <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                <Sparkles size={48} className="text-blue-600" />
              </div>
              <h2 className={`text-3xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>Choose Your Path</h2>
              <p className={`${isDark ? "text-gray-400" : "text-gray-600"}`}>
                To access Elite Technology Mastery, you must first select the technology you wish to master. 
                Remember, you can only choose <span className="font-bold">one</span>.
              </p>
              <button 
                onClick={() => setShowSelectionModal(true)}
                className="px-8 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200"
              >
                Select Technology Now
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Sidebar */}
            <div className={`transition-all duration-300 ease-in-out ${isSidebarOpen ? 'w-80' : 'w-0 opacity-0 pointer-events-none'} border-r overflow-y-auto custom-scrollbar ${isDark ? "bg-[#0B0F1A] border-white/5" : "bg-white border-gray-200"}`}>
              <SidebarTechTree
                data={filteredTechnologies}
                onSelectSubtopic={(file, isLocked) => handleFileSelect(file, isLocked)}
                theme={theme}
                selectedFile={selected.subtopic}
                isPaid={hasPremiumAccess}
              />
            </div>

            {/* Content Area */}
            <div id="mastery-content-area" className="flex-1 overflow-y-auto transition-all duration-300 ease-in-out">
              {!selected.subtopic ? (
                <div className={`h-full flex items-center justify-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  <div className="text-center space-y-4">
                    <div className={`mx-auto w-20 h-20 rounded-2xl flex items-center justify-center ${isDark ? "bg-white/5" : "bg-gray-100"}`}>
                      <Sparkles size={40} className="text-blue-500" />
                    </div>
                    <div>
                      <h3 className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                        Select a topic to start learning
                      </h3>
                      <p className="text-sm mt-2">
                        Master {tech?.replace('-premium', '')} with our elite documentation
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-8 max-w-5xl mx-auto">
                  <EliteMarkdownViewer 
                    filePath={selected.subtopic} 
                    locked={lockedFile === selected.subtopic && !hasPremiumAccess}
                    onHeadingsCollected={setHeadings}
                    activeHeading={activeHeading}
                    setActiveHeading={setActiveHeading}
                    headings={headings}
                    readingProgress={readingProgress}
                    setReadingProgress={setReadingProgress}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ChatBot Integration */}
      {showChat && (
        <div ref={chatBotRef} className="fixed bottom-6 right-6 z-[100]">
          <ChatBot onClose={() => setShowChat(false)} />
        </div>
      )}

      {/* Voice Assistant Integration */}
      {showVoice && (
        <div ref={voiceAssistantRef} className="fixed bottom-20 right-6 z-[100] w-80 max-w-[90vw] h-[500px] max-h-[80vh] flex flex-col shadow-2xl rounded-2xl overflow-hidden border border-white/10 bg-[#0F172A]">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-3 flex justify-between items-center text-white shrink-0">
             <div className="flex items-center gap-2">
                <Mic size={18} />
                <span className="font-bold text-xs">AI Voice Assistant</span>
             </div>
             <button onClick={() => setShowVoice(false)} className="hover:bg-white/20 p-1 rounded transition-colors">
                <ChevronDown size={20} />
             </button>
          </div>
          <div className="flex-1 overflow-y-auto custom-scrollbar bg-slate-900/50">
            <NewVoiceAssistant compact={true} />
          </div>
        </div>
      )}

      {/* Selection Modal for Free Users (First Visit) */}
      <div className="relative z-[2000]">
        <EliteSelectionModal
          isOpen={showSelectionModal}
          onClose={() => {
            setShowSelectionModal(false);
            navigate('/dashboard');
          }}
          userId={getUserId()}
          onSelectionSuccess={handleSelectionSuccess}
        />
      </div>

      {/* Premium Upgrade Modal */}
      <PremiumUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="Unlock All Elite Technologies"
        message="Upgrade to premium to access all Elite Technology documentation without limits. View both Java Premium and Python Premium with full content access."
      />
    </div>
  );
};

export default EliteTechnologyDetail;
